export type AppEnvironment = 'development' | 'staging' | 'production';

const BANNER_COLORS = {
  override: 'bg-red-500/90 text-red-950',
  staging: 'bg-orange-500/90 text-orange-950',
  development: 'bg-stone-500/90 text-stone-950',
} as const;

/** Map the office app env onto GET /health `environment` (dev | staging | prod). */
export function toApiEnvironmentName(env: AppEnvironment): 'dev' | 'staging' | 'prod' {
  if (env === 'development') return 'dev';
  if (env === 'production') return 'prod';
  return 'staging';
}

function fromViteEnvironment(value: string | undefined): AppEnvironment | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'development' || v === 'dev') return 'development';
  if (v === 'staging') return 'staging';
  if (v === 'production' || v === 'prod') return 'production';
  return null;
}

/**
 * Prefer VITE_ENVIRONMENT (set at deploy). Hostname fallback treats `dev.*` as
 * development — `dev.app.getruta.com` is not production.
 */
export function resolveAppEnvironment(
  hostname: string,
  viteEnvironment?: string,
): AppEnvironment {
  const fromVite = fromViteEnvironment(viteEnvironment);
  if (fromVite) return fromVite;

  if (hostname.startsWith('staging.')) return 'staging';
  if (
    hostname.startsWith('dev.') ||
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1')
  ) {
    return 'development';
  }
  return 'production';
}

/**
 * Green Valley one-tap logins — office equivalent of the crew-app quick login.
 * Fail closed: a production *build*, a production *host*, or the production API
 * all hide it. Credentials stay in the client either way; this is the UI door.
 */
export function showDemoLogins(
  hostname: string,
  viteEnvironment?: string,
  apiUrl?: string,
): boolean {
  if (resolveAppEnvironment(hostname, viteEnvironment) === 'production') return false;
  if (/^https:\/\/api\.getruta\.com\b/.test(apiUrl || '')) return false;
  return true;
}

export function environmentBanner(
  appEnv: AppEnvironment,
  apiEnv: string | null,
): { label: string; colors: string } | null {
  const isOverride = !!apiEnv && apiEnv !== toApiEnvironmentName(appEnv);
  if (appEnv === 'production' && !isOverride) return null;
  const colors = isOverride
    ? BANNER_COLORS.override
    : appEnv === 'staging'
      ? BANNER_COLORS.staging
      : BANNER_COLORS.development;
  return { label: `${appEnv}${isOverride ? ` → ${apiEnv} data` : ''}`, colors };
}
